import classNames from 'classnames';
import I18n from 'i18n-js';
import { debounce } from 'lodash';
import React from 'react';

import SanitizedRender from '../components/sanitized_render';
import { parseAmount } from '../money';
import { UserInputOffer } from './negotiation_types';
import { core_apimessages_Condition } from '../rql';
import PriceDisplay from '../components/price_display';

interface IProps {
  offer: UserInputOffer;
  onOfferChange: (any) => void;
  originalPrice: string;
  currency: string;
  recommendedPriceDropPercent?: number;
  listingCondition?: core_apimessages_Condition;
  preventLowballOffers?: boolean;
  wouldBeAutoRejected?: boolean;
  modalDialogVersion?: boolean;
}

const VERY_LOW_DISCOUNT_PERCENT = 35;
const HIGH_DISCOUNT_PERCENT = 0;
const DEFAULT_RECOMMENDED_DISCOUNT_A = 10;
const DEFAULT_RECOMMENDED_DISCOUNT_B = 15;
const DEBOUNCE_DELAY = 300;

export class RecommendedOfferNudge extends React.Component<IProps, null> {
  debouncedUpdate = debounce(this.forceUpdate, DEBOUNCE_DELAY);

  shouldComponentUpdate(nextProps) {
    if (!nextProps.offer.price) {
      return true;
    }
    this.debouncedUpdate();
    return false;
  }

  componentWillUnmount() {
    this.debouncedUpdate.flush();
  }

  buttonPercentOptions() {
    if (this.props.recommendedPriceDropPercent) {
      return [this.props.recommendedPriceDropPercent];
    }

    return [DEFAULT_RECOMMENDED_DISCOUNT_A, DEFAULT_RECOMMENDED_DISCOUNT_B];
  }

  getOfferTip() {
    if (this.props.offer.price === '') { return ''; }

    const originalPrice = parseFloat(this.props.originalPrice);
    const offerPrice = parseFloat(this.props.offer.price);
    const percentOff = Math.round((1 - (offerPrice / originalPrice)) * 100);

    if (this.props.preventLowballOffers) {
      if (this.props.wouldBeAutoRejected) {
        return I18n.t('commons.offers.wouldBeAutoRejected');
      }
    } else if (percentOff >= VERY_LOW_DISCOUNT_PERCENT) {
      return I18n.t('commons.offers.offerTipLowball');
    }

    if (percentOff < HIGH_DISCOUNT_PERCENT) {
      return I18n.t('commons.offers.offerTipHighball');
    }

    return '';
  }

  getDiscountedPrice(discount) {
    const amount = parseFloat(this.props.originalPrice);
    return Math.round(amount * ((100 - discount) / 100)).toString();
  }

  handleClick(e, discount) {
    e.preventDefault();
    const price = this.getDiscountedPrice(discount);

    if (this.props.modalDialogVersion) {
      this.props.onOfferChange({ type: 'setPrice', value: price });
    } else {
      this.props.onOfferChange({ ...this.props.offer, price });
    }
  }

  buttonClass(discount) {
    return classNames(
      'button button--small mlr-half mtb-half',
      {
        'button--primary': this.props.offer.price === this.getDiscountedPrice(discount),
        'fx-grow': !this.props.recommendedPriceDropPercent,
      },
    );
  }

  buttonContent(discount) {
    if (this.props.recommendedPriceDropPercent) {
      const { amountCents, currency, display } = parseAmount(this.getDiscountedPrice(discount), this.props.currency);
      return (
        <>
          <PriceDisplay
            amountCents={amountCents}
            currency={currency}
            display={display}
          />
          <span> ({I18n.t('commons.offers.percentOff', { percent: discount })})</span>
        </>
      );
    }

    return I18n.t('commons.offers.percentOff', { percent: discount });
  }

  renderOfferTip(offerTip) {
    if (offerTip === '') { return false; }

    return (
      <div className="mb-1 weight-bold align-left icon-l-exclamation-circle first-time-offer-tip">
        {offerTip}
      </div>
    );
  }

  renderRecommendedOfferExplainer() {
    if (!this.props.recommendedPriceDropPercent) { return null; }

    return (
      <div className="mobile-mb-half mr-1 mt-1 align-left">
        <SanitizedRender
          html={I18n.t('commons.offers.recommendedDropExplainer', {
            conditionName: this.props.listingCondition.displayName.toLowerCase(),
          })}
        />
      </div>
    );
  }

  render() {
    const offerTip = this.getOfferTip();

    const classes = classNames('feature-nudge scaling-mb-2', {
      'feature-nudge--orange': offerTip !== '',
    });

    return (
      <div className={classes}>
        {this.renderOfferTip(offerTip)}
        <div className="d-flex fx-align-center mobile-d-block">
          <div className="mobile-mb-half mr-1 align-left recommended-offers-title">
            {this.props.recommendedPriceDropPercent ? I18n.t('commons.offers.recommendedOffer') : I18n.t('commons.offers.quickOffers')}
          </div>
          <div className="d-flex fx-wrap fx-grow" style={{ margin: '-0.5rem' }}>
            {this.buttonPercentOptions().map((discount) => (
              <button
                key={discount}
                className={this.buttonClass(discount)}
                onClick={(e) => this.handleClick(e, discount)}
                type="button"
              >
                {this.buttonContent(discount)}
              </button>
            ))}
          </div>
        </div>
        {this.renderRecommendedOfferExplainer()}
      </div>
    );
  }
}

export default RecommendedOfferNudge;
